import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { calculatePaces, EVENTS, parseTimeToSeconds, applyAltitudeAdjustment, formatTime, pickPaceBenchmark } from '../../lib/vdot';
import { isRelayEvent, relaySplitEvent, getRelayLegFor } from '../../lib/relays';
import { resolvePaceTags, renderWorkoutText } from '../../lib/paceTags';
import { parseRunningMiles, parseTimedIntervalMiles, parseCrossTrainingMiles, parseCrossTrainingMilesRange, paceStringToSeconds, formatVolume, formatVolumeRange } from '../../lib/volumeParser';
import { findBestSplits, buildWeekBars } from '../../lib/workoutSegmentParser';
import { QUAL_STANDARDS, checkQualifiers, formatStdTime } from '../../lib/qualStandards';
import { TrainingLogModal } from '../athlete/TrainingLogModal';
import { futureBillingMonthsThrough, futureTrainingAccessIsOpenForBillingRows } from '../../lib/billing';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekDates(referenceDate) {
  const d = new Date(referenceDate);
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(d);
    dt.setDate(d.getDate() + i);
    return dt;
  });
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function weekRangeLabel(weekOffset) {
  const ref = new Date();
  ref.setDate(ref.getDate() + weekOffset * 7);
  const days = getWeekDates(ref);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(days[0])} \u2013 ${fmt(days[6])}`;
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const race = new Date(dateStr + 'T00:00:00');
  return Math.round((race - today) / (1000 * 60 * 60 * 24));
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Multi-day aware date formatter — collapses same-month ranges. */
function formatRaceDateRange(startStr, endStr) {
  if (!startStr) return '';
  if (!endStr || endStr === startStr) return formatRaceDate(startStr);
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    const mon = start.toLocaleDateString('en-US', { month: 'short' });
    return `${mon} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getEventLabels(eventField) {
  if (!eventField) return '';
  try {
    const parsed = JSON.parse(eventField);
    if (Array.isArray(parsed)) return parsed.map(e => EVENTS[e]?.label ?? e).join(', ');
  } catch {}
  return EVENTS[eventField]?.label ?? eventField;
}

const GEAR_ITEM_TYPES = ['Shoes', 'Spikes', 'Racing Flats', 'Shirt', 'Shorts', 'Pants', 'Socks', 'Jacket', 'Hat', 'Other'];

const STATUS_STYLES = {
  pending:   { background: 'var(--gray-100)', color: 'var(--gray-500)' },
  approved:  { background: '#dbeafe', color: '#1d4ed8' },
  ordered:   { background: '#fef9c3', color: '#92400e' },
  delivered: { background: '#d1fae5', color: '#065f46' },
  denied:    { background: '#fee2e2', color: '#991b1b' },
};


export function ProDashboard() {
  const { profile } = useAuth();
  const { pathname } = useLocation();

  // Derive active tab from URL path
  const activeTab = pathname.startsWith('/pro/standards') ? 'standards'
    : pathname.startsWith('/pro/gear') ? 'gear'
    : pathname.startsWith('/pro/profile') ? 'profile'
    : 'training';

  // Athlete profile data
  const [gender, setGender] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Training tab state
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekWorkouts, setWeekWorkouts] = useState([]);
  const [week2Workouts, setWeek2Workouts] = useState([]);
  const [trainingLogs, setTrainingLogs] = useState({});
  const [trainingLogs2, setTrainingLogs2] = useState({});
  const [trainingLogSessions, setTrainingLogSessions] = useState({});
  const [trainingLogSessions2, setTrainingLogSessions2] = useState({});
  const [loading, setLoading] = useState(true);
  const [loading2, setLoading2] = useState(true);
  const [paces, setPaces] = useState(null);
  const [logModalDay, setLogModalDay] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [bestSplits, setBestSplits] = useState({});
  const [nextRace, setNextRace] = useState(null);
  const [pendingResults, setPendingResults] = useState([]);
  const [resultInputs, setResultInputs] = useState({});
  const [resultSaving, setResultSaving] = useState(null);
  const [resultErrors, setResultErrors] = useState({});

  // Coach info
  const [coachName, setCoachName] = useState(null);
  const [coachAvatarUrl, setCoachAvatarUrl] = useState(null);
  const [coachBio, setCoachBio] = useState(null);
  const [coachCredentials, setCoachCredentials] = useState(null);

  // Vacation state
  const [vacations, setVacations] = useState([]);
  const [showVacForm, setShowVacForm] = useState(false);
  const [vacStart, setVacStart] = useState('');
  const [vacEnd, setVacEnd] = useState('');
  const [vacEquipment, setVacEquipment] = useState([]);
  const [vacBlackouts, setVacBlackouts] = useState([]);
  const [vacNotes, setVacNotes] = useState('');
  const [vacSaving, setVacSaving] = useState(false);
  const [vacError, setVacError] = useState('');

  // General comments
  const [proComments, setProComments] = useState('');
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [mediaError, setMediaError] = useState('');

  // Standards tab
  const [benchmarks, setBenchmarks] = useState([]);
  const [qualifiers, setQualifiers] = useState([]);
  const [stdGenderFilter, setStdGenderFilter] = useState('all');

  // Gear tab
  const [gearRequests, setGearRequests] = useState([]);
  const [gearItemType, setGearItemType] = useState('Shoes');
  const [gearModel, setGearModel] = useState('');
  const [gearSize, setGearSize] = useState('');
  const [gearNotes, setGearNotes] = useState('');
  const [gearSaving, setGearSaving] = useState(false);
  const [gearError, setGearError] = useState('');

  // Profile tab - media
  const [proMedia, setProMedia] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaCaptions, setMediaCaptions] = useState({});
  const [usatfChecked, setUsatfChecked] = useState(false);

  // ── Initial loads ─────────────────────────────────────────────────
  useEffect(() => {
    if (!profile) return;
    loadAthleteProfile();
    loadNextRace();
    loadPendingResults();
    loadHistoryLogs();
    loadVacations();
    loadCoachProfile();
    loadBenchmarks();
    loadGearRequests();
    loadProMedia();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load both weeks when offset changes
  useEffect(() => {
    if (profile) {
      loadWeek(weekOffset, false);
      loadWeek(weekOffset + 1, true);
    }
  }, [profile, weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBestSplits(findBestSplits(historyLogs));
  }, [historyLogs]);

  useEffect(() => {
    if (benchmarks.length && gender) {
      setQualifiers(checkQualifiers(benchmarks, gender));
    }
  }, [benchmarks, gender]);

  async function loadAthleteProfile() {
    const { data } = await supabase
      .from('athlete_profiles')
      .select('grade_level, gender, coach_id, usatf_apex_associated, pro_comments, avatar_url')
      .eq('id', profile.id)
      .maybeSingle();
    if (data) {
      setGender(data.gender ?? null);
      setAvatarUrl(data.avatar_url ?? null);
      setProComments(data.pro_comments ?? '');
      setUsatfChecked(data.usatf_apex_associated ?? false);
    }
  }

  async function loadCoachProfile() {
    const { data: ap } = await supabase
      .from('athlete_profiles').select('coach_id').eq('id', profile.id).maybeSingle();
    if (!ap?.coach_id) return;
    const [{ data: coachProf }, { data: coachExtra }] = await Promise.all([
      supabase.from('profiles').select('full_name').eq('id', ap.coach_id).single(),
      supabase.from('coach_profiles').select('avatar_url, bio, credentials').eq('id', ap.coach_id).maybeSingle(),
    ]);
    if (coachProf?.full_name) setCoachName(coachProf.full_name);
    if (coachExtra?.avatar_url) setCoachAvatarUrl(coachExtra.avatar_url);
    if (coachExtra?.bio) setCoachBio(coachExtra.bio);
    if (coachExtra?.credentials) setCoachCredentials(coachExtra.credentials);
  }

  async function loadNextRace() {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase.from('races').select('*')
      .eq('athlete_id', profile.id).gte('race_date', today)
      .order('race_date', { ascending: true }).limit(1);
    setNextRace(data?.[0] ?? null);
  }

  async function loadPendingResults() {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase.from('races').select('*')
      .eq('athlete_id', profile.id).lt('race_date', today)
      .is('result_time_seconds', null).order('race_date', { ascending: false }).limit(5);
    setPendingResults(data ?? []);
  }

  async function loadHistoryLogs() {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 112);
    const { data } = await supabase.from('training_logs')
      .select('log_date, segments, total_miles').eq('athlete_id', profile.id)
      .gte('log_date', toDateStr(cutoff)).order('log_date', { ascending: true });
    setHistoryLogs(data ?? []);
  }

  async function loadVacations() {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase.from('athlete_vacations')
      .select('id, start_date, end_date, available_equipment, blackout_dates, notes')
      .eq('athlete_id', profile.id).gte('end_date', today)
      .order('start_date', { ascending: true });
    setVacations(data ?? []);
  }

  async function loadBenchmarks() {
    const { data } = await supabase.from('benchmarks').select('*')
      .eq('athlete_id', profile.id).order('race_date', { ascending: false });
    setBenchmarks(data ?? []);
  }

  async function loadGearRequests() {
    const { data } = await supabase.from('pro_gear_requests').select('*')
      .eq('athlete_id', profile.id).order('created_at', { ascending: false });
    setGearRequests(data ?? []);
  }

  async function loadProMedia() {
    const { data } = await supabase.from('pro_media').select('*')
      .eq('athlete_id', profile.id).order('created_at', { ascending: false });
    if (data) {
      setProMedia(data);
      const caps = {};
      data.forEach(m => { caps[m.id] = m.caption ?? ''; });
      setMediaCaptions(caps);
    }
  }

  async function loadWeek(offset, isSecondWeek) {
    if (isSecondWeek) setLoading2(true);
    else setLoading(true);

    const ref = new Date();
    ref.setDate(ref.getDate() + offset * 7);
    const weekDates = getWeekDates(ref);
    const todayStr = toDateStr(new Date());
    const dateStrings = weekDates.map(toDateStr);

    const { data: logData } = await supabase.from('training_logs')
      .select('id, log_date, segments, total_miles, running_miles, xt_miles, notes, session')
      .eq('athlete_id', profile.id).in('log_date', dateStrings);
    const sessionsMap = {};
    for (const row of logData ?? []) {
      const date = row.log_date;
      const sess = row.session ?? 'full';
      if (!sessionsMap[date]) sessionsMap[date] = {};
      const existing = sessionsMap[date][sess];
      if (!existing || row.id > existing.id) sessionsMap[date][sess] = row;
    }
    const logsMap = {};
    for (const [date, dateSessions] of Object.entries(sessionsMap)) {
      for (const row of Object.values(dateSessions)) {
        if (!logsMap[date]) logsMap[date] = { segments: [], total_miles: 0, running_miles: 0, xt_miles: 0, notes: row.notes };
        logsMap[date].segments = [...logsMap[date].segments, ...(row.segments ?? [])];
        logsMap[date].total_miles += row.total_miles ?? 0;
        logsMap[date].running_miles += row.running_miles ?? row.total_miles ?? 0;
        logsMap[date].xt_miles += row.xt_miles ?? 0;
      }
    }

    const { data: weekRaces } = await supabase.from('races')
      .select('id, name, event, race_date, priority')
      .eq('athlete_id', profile.id).in('race_date', dateStrings);
    const racesMap = {};
    (weekRaces ?? []).forEach(r => { racesMap[r.race_date] = r; });

    const { data: assignments } = await supabase.from('athlete_plan_assignments')
      .select('plan_id, plans(id, month, year, status)').eq('athlete_id', profile.id);

    const accessCache = {};
    async function monthIsOpen(month, year) {
      const key = `${year}-${month}`;
      if (accessCache[key] != null) return accessCache[key];
      for (const futureMonth of futureBillingMonthsThrough(month, year)) {
        const futureMatch = (assignments ?? [])
          .filter(a => a.plans?.month === futureMonth.month && a.plans?.year === futureMonth.year)
          .reduce((best, a) => !best || a.plans.id > best.plans.id ? a : best, null);
        const { data: billingRows } = await supabase.from('billing')
          .select('paid_at')
          .eq('athlete_id', profile.id)
          .eq('month', futureMonth.month)
          .eq('year', futureMonth.year);
        if (!futureTrainingAccessIsOpenForBillingRows(futureMatch?.plans, billingRows)) {
          accessCache[key] = false;
          return false;
        }
      }
      accessCache[key] = true;
      return true;
    }

    const planByDate = {};
    for (const d of weekDates) {
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      if (!(await monthIsOpen(month, year))) continue;
      const match = (assignments ?? [])
        .filter(a => a.plans?.month === month && a.plans?.year === year)
        .reduce((best, a) => !best || a.plans.id > best.plans.id ? a : best, null);
      if (match?.plans?.id) planByDate[toDateStr(d)] = match.plans.id;
    }

    const planIds = [...new Set(Object.values(planByDate))];
    const { data: planWorkouts } = planIds.length
      ? await supabase.from('plan_workouts')
        .select('plan_id, workout_date, custom_description, coach_notes')
        .in('plan_id', planIds).in('workout_date', dateStrings)
      : { data: [] };
    const workoutMap = {};
    const coachNoteMap = {};
    (planWorkouts ?? []).forEach(w => {
      if (planByDate[w.workout_date] !== w.plan_id) return;
      workoutMap[w.workout_date] = w.custom_description ?? '';
      if (w.coach_notes) coachNoteMap[w.workout_date] = w.coach_notes;
    });

    const [{ data: bmarks }, { data: apAlt }] = await Promise.all([
      supabase.from('benchmarks').select('id, event, time_seconds')
        .eq('athlete_id', profile.id).order('race_date', { ascending: false }).limit(20),
      supabase.from('athlete_profiles').select('training_altitude_ft, pinned_benchmark_id')
        .eq('id', profile.id).maybeSingle(),
    ]);

    let localPaces = null;
    let localEasyPaceSec = null;
    const bmark = pickPaceBenchmark(bmarks, apAlt?.pinned_benchmark_id);
    if (bmark) {
      const result = calculatePaces(bmark.event, bmark.time_seconds);
      localPaces = result?.paces ?? null;
      if (localPaces) localPaces = applyAltitudeAdjustment(localPaces, apAlt?.training_altitude_ft ?? null);
      if (localPaces?.easy?.perMile) localEasyPaceSec = paceStringToSeconds(localPaces.easy.perMile);
    }
    if (!isSecondWeek) setPaces(localPaces);

    const workouts = weekDates.map((d, i) => {
      const dateStr = toDateStr(d);
      const rawText = workoutMap[dateStr] ?? '';
      const resolvedText = localPaces ? resolvePaceTags(rawText, localPaces) : rawText;
      const runMiles = parseRunningMiles(rawText) + parseTimedIntervalMiles(rawText, localPaces);
      const xtMiles = parseCrossTrainingMiles(rawText, localEasyPaceSec);
      const xtRange = localEasyPaceSec ? parseCrossTrainingMilesRange(rawText, localEasyPaceSec) : null;
      return {
        date: dateStr, dayLabel: DAY_LABELS[i], dayNum: d.getDate(),
        workout: resolvedText, rawWorkout: rawText,
        coachNote: coachNoteMap[dateStr] ?? '',
        runMiles, xtMiles, xtRange,
        race: racesMap[dateStr] ?? null, isToday: dateStr === todayStr,
      };
    });

    if (isSecondWeek) { setTrainingLogs2(logsMap); setTrainingLogSessions2(sessionsMap); setWeek2Workouts(workouts); setLoading2(false); }
    else { setTrainingLogs(logsMap); setTrainingLogSessions(sessionsMap); setWeekWorkouts(workouts); setLoading(false); }
  }

  function handleLogSaved(logData) {
    const { log_date, session } = logData;
    const sess = session ?? 'full';
    const mergeLogs = (prev, prevSessions) => {
      const existing = prev[log_date] ?? { segments: [], total_miles: 0, running_miles: 0, xt_miles: 0, notes: '' };
      const oldSessSegs = (prevSessions[log_date]?.[sess]?.segments ?? []);
      const oldSessMiles = prevSessions[log_date]?.[sess]?.total_miles ?? 0;
      const oldSessRunMiles = prevSessions[log_date]?.[sess]?.running_miles ?? oldSessMiles;
      const oldSessXtMiles = prevSessions[log_date]?.[sess]?.xt_miles ?? 0;
      const newSegs = [...existing.segments.filter(s => (s.session ?? 'full') !== sess), ...(logData.segments ?? [])];
      const newMiles = (existing.total_miles - oldSessMiles) + (logData.total_miles ?? 0);
      const newRunMiles = (existing.running_miles - oldSessRunMiles) + (logData.running_miles ?? logData.total_miles ?? 0);
      const newXtMiles = (existing.xt_miles - oldSessXtMiles) + (logData.xt_miles ?? 0);
      return { ...existing, segments: newSegs, total_miles: Math.max(0, newMiles), running_miles: Math.max(0, newRunMiles), xt_miles: Math.max(0, newXtMiles) };
    };
    setTrainingLogSessions(prev => {
      const updated = { ...prev, [log_date]: { ...(prev[log_date] ?? {}), [sess]: logData } };
      setTrainingLogs(prevLogs => ({ ...prevLogs, [log_date]: mergeLogs(prevLogs, prev) }));
      return updated;
    });
    setTrainingLogSessions2(prev => {
      const updated = { ...prev, [log_date]: { ...(prev[log_date] ?? {}), [sess]: logData } };
      setTrainingLogs2(prevLogs => ({ ...prevLogs, [log_date]: mergeLogs(prevLogs, prev) }));
      return updated;
    });
    setHistoryLogs(prev => {
      const without = prev.filter(l => l.log_date !== log_date);
      return [...without, { log_date, segments: logData.segments, total_miles: logData.total_miles }]
        .sort((a, b) => (a.log_date < b.log_date ? -1 : 1));
    });
  }

  async function handleLogResult(race) {
    const timeStr = resultInputs[race.id] ?? '';
    const seconds = parseTimeToSeconds(timeStr);
    if (!seconds) {
      setResultErrors(prev => ({ ...prev, [race.id]: 'Enter a valid time (e.g. 4:32.56 or 16:45.00)' }));
      return;
    }
    setResultErrors(prev => ({ ...prev, [race.id]: '' }));
    setResultSaving(race.id);
    await supabase.from('races').update({ result_time_seconds: seconds }).eq('id', race.id);
    // Races created via the multi-event UI store `event` as a JSON-stringified
    // array (e.g. '["1600m"]'). Unwrap to the first event before applying the
    // relay-split mapping so the benchmark gets a key in EVENTS.
    let primaryEvent = race.event;
    if (typeof primaryEvent === 'string' && primaryEvent.startsWith('[')) {
      try {
        const parsed = JSON.parse(primaryEvent);
        if (Array.isArray(parsed) && parsed.length > 0) primaryEvent = parsed[0];
      } catch {}
    }
    // Map relay race events to their split pseudo-event for the
    // benchmark. Per-relay leg lookup in case race.event is a relay
    // and the leg lives in race.relay_legs.
    const bmEvent = isRelayEvent(primaryEvent)
      ? (relaySplitEvent(primaryEvent, getRelayLegFor(race, primaryEvent)) ?? primaryEvent)
      : primaryEvent;
    // Dedupe on re-save: drop any prior row for this slot before the
    // fresh insert.
    await supabase.from('benchmarks')
      .delete()
      .eq('athlete_id', profile.id)
      .eq('event', bmEvent)
      .eq('race_date', race.race_date)
      .eq('notes', race.name);
    await supabase.from('benchmarks').insert({
      athlete_id: profile.id,
      event: bmEvent,
      time_seconds: seconds,
      race_date: race.race_date,
      notes: race.name,
      season: race.season ?? null,
    });
    setResultSaving(null);
    setResultInputs(prev => ({ ...prev, [race.id]: '' }));
    await loadPendingResults();
    await loadNextRace();
    await loadBenchmarks();
  }

  async function handleAddVacation(e) {
    e.preventDefault();
    setVacError('');
    if (!vacStart || !vacEnd) { setVacError('Please enter start and end dates.'); return; }
    if (vacEnd < vacStart) { setVacError('End date must be on or after start date.'); return; }
    setVacSaving(true);
    const { error: err } = await supabase.from('athlete_vacations').insert({
      athlete_id: profile.id, start_date: vacStart, end_date: vacEnd,
      available_equipment: vacEquipment, blackout_dates: vacBlackouts,
      notes: vacNotes.trim() || null,
    });
    if (err) { setVacError(err.message); setVacSaving(false); return; }
    setVacStart(''); setVacEnd(''); setVacEquipment([]); setVacBlackouts([]); setVacNotes('');
    setShowVacForm(false);
    await loadVacations();
    setVacSaving(false);
  }

  async function handleDeleteVacation(vacId) {
    await supabase.from('athlete_vacations').delete().eq('id', vacId);
    setVacations(prev => prev.filter(v => v.id !== vacId));
  }

  async function handleSaveComments() {
    setCommentsSaving(true);
    const { error: err } = await supabase.from('athlete_profiles').upsert({ id: profile.id, pro_comments: proComments });
    if (err) console.error('handleSaveComments:', err.message, err.code);
    setCommentsSaving(false);
  }

  async function handleGearSubmit(e) {
    e.preventDefault();
    setGearError('');
    setGearSaving(true);
    const { error: err } = await supabase.from('pro_gear_requests').insert({
      athlete_id: profile.id, item_type: gearItemType,
      model: gearModel || null,
      size: gearSize || null, notes: gearNotes || null,
      status: 'pending',
    });
    if (err) { setGearError(err.message); setGearSaving(false); return; }
    setGearModel(''); setGearSize(''); setGearNotes(''); setGearItemType('Shoes');
    await loadGearRequests();
    setGearSaving(false);
  }

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${profile.id}.${ext}`;
    await supabase.storage.from('avatars').upload(path, file, { upsert: true });
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const url = `${urlData.publicUrl}?t=${Date.now()}`;
    const { error: upErr } = await supabase.from('athlete_profiles').upsert({ id: profile.id, avatar_url: url });
    if (upErr) console.error('handleAvatarUpload profile upsert:', upErr.message, upErr.code);
    setAvatarUrl(url);
    setAvatarUploading(false);
  }

  async function handleMediaUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaUploading(true);
    setMediaError('');
    const ext = file.name.split('.').pop();
    const path = `${profile.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('pro_media').upload(path, file, { upsert: false });
    if (upErr) { setMediaError(upErr.message); setMediaUploading(false); return; }
    const { data: urlData } = supabase.storage.from('pro_media').getPublicUrl(path);
    await supabase.from('pro_media').insert({ athlete_id: profile.id, file_url: urlData.publicUrl, caption: null });
    await loadProMedia();
    setMediaUploading(false);
  }

  async function handleMediaCaptionBlur(mediaId) {
    const caption = mediaCaptions[mediaId] ?? '';
    await supabase.from('pro_media').update({ caption }).eq('id', mediaId);
  }

  async function handleDeleteMedia(mediaId) {
    await supabase.from('pro_media').delete().eq('id', mediaId);
    setProMedia(prev => prev.filter(m => m.id !== mediaId));
  }

  async function handleUsatfChange(checked) {
    setUsatfChecked(checked);
    const { error: err } = await supabase.from('athlete_profiles').upsert({ id: profile.id, usatf_apex_associated: checked });
    if (err) console.error('handleUsatfChange:', err.message, err.code);
  }

  // ── Derived data ──────────────────────────────────────────────────
  const weekBars = buildWeekBars(historyLogs);
  const maxBarMiles = Math.max(...weekBars.map(w => w.miles), 1);
  const todayStr = toDateStr(new Date());

  const actualWeekMiles = Object.values(trainingLogs).reduce((s, l) => s + (l.total_miles || 0), 0);
  const actualTodayMiles = weekOffset === 0 ? (trainingLogs[todayStr]?.total_miles || 0) : 0;
  const currentMonthStr = todayStr.slice(0, 7);
  const actualMonthMiles = historyLogs
    .filter(l => l.log_date.startsWith(currentMonthStr))
    .reduce((s, l) => s + (l.total_miles || 0), 0);

  // ── Week section renderer ─────────────────────────────────────────
  function renderWeekSection(workouts, logs, sessions, isSecondWeek, isLoadingWeek) {
    const offset = isSecondWeek ? weekOffset + 1 : weekOffset;
    const weekRunMiles = workouts.reduce((s, d) => s + (d.runMiles || 0), 0);
    const weekXtMiles = workouts.reduce((s, d) => s + (d.xtMiles || 0), 0);
    const weekTotal = weekRunMiles + weekXtMiles;
    const weekHasXt = weekXtMiles > 0.01;
    const actualMiles = Object.values(logs).reduce((s, l) => s + (l.total_miles || 0), 0);

    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{
          padding: '12px 24px', borderBottom: '1px solid var(--gray-100)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>
            {isSecondWeek ? 'Next Week' : 'This Week'}{' '}
            <span style={{ fontWeight: 400, color: 'var(--gray-400)', fontSize: 12 }}>
              {weekRangeLabel(offset)}
            </span>
          </span>
          {weekTotal > 0.01 && (
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
              {weekHasXt
                ? `Run: ${formatVolume(weekRunMiles)} · XT: ${formatVolume(weekXtMiles)} · Total: ${formatVolume(weekTotal)}`
                : `Total: ${formatVolume(weekTotal)}`}
            </span>
          )}
        </div>

        {!isSecondWeek && actualMiles > 0.001 && (
          <div className="week-actual-volume">
            <span className="wav-label">Logged</span>
            {actualTodayMiles > 0.001 && weekOffset === 0 && (
              <span>Today: <strong>{formatVolume(actualTodayMiles)}</strong></span>
            )}
            <span>Week: <strong>{formatVolume(actualMiles)}</strong></span>
            {actualMonthMiles > 0.001 && (
              <span>Month: <strong>{formatVolume(actualMonthMiles)}</strong></span>
            )}
          </div>
        )}

        {isLoadingWeek ? (
          <div className="loading-screen" style={{ minHeight: 80 }}>
            <div className="loading-dot-row">
              <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
            </div>
          </div>
        ) : (
          <div className="week-grid">
            {workouts.map(day => {
              const log = logs[day.date];
              return (
                <div
                  key={day.date}
                  className={`week-day-card${day.isToday ? ' week-day-card--today' : ''}`}
                >
                  <div className="week-day-name">{day.dayLabel}</div>
                  <div className="week-day-date">{day.dayNum}</div>

                  {day.race && (
                    <div className="day-race-badge" title={day.race.name}>
                      <span className="day-race-flag">&#127937;</span>
                      <span className="day-race-name">{day.race.name}</span>
                    </div>
                  )}

                  {log && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <div className="day-logged-badge" style={{ marginBottom: 0 }}>Logged</div>
                      {(log.running_miles || 0) > 0.001 && (log.xt_miles || 0) > 0.001 ? (
                        <>
                          <span style={{ fontSize: 11, color: 'var(--navy)' }}>Run: {formatVolume(log.running_miles)}</span>
                          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>XT: {formatVolume(log.xt_miles)}</span>
                          <div className="day-actual-miles">{formatVolume(log.total_miles)}</div>
                        </>
                      ) : (log.xt_miles || 0) > 0.001 && (log.running_miles || 0) <= 0.001 ? (
                        <div className="day-actual-miles" style={{ color: 'var(--gray-400)' }}>XT: {formatVolume(log.xt_miles)}</div>
                      ) : (log.total_miles || 0) > 0.001 ? (
                        <div className="day-actual-miles">{formatVolume(log.total_miles)}</div>
                      ) : null}
                    </div>
                  )}

                  <div className="week-day-workout">
                    {day.workout ? renderWorkoutText(day.workout) : <span className="cell-muted">Rest</span>}
                  </div>

                  {(() => {
                    const total = (day.runMiles || 0) + (day.xtMiles || 0);
                    if (total < 0.01) return null;
                    const hasXt = (day.xtMiles || 0) > 0.01;
                    if (!hasXt) return <div className="day-volume"><strong>{formatVolume(total)}</strong></div>;
                    const xtLabel = day.xtRange ? formatVolumeRange(day.xtRange) : formatVolume(day.xtMiles);
                    return (
                      <div className="day-volume" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(day.runMiles || 0) > 0.01 && <span style={{ color: 'var(--navy)', fontSize: 11 }}>Run: {formatVolume(day.runMiles)}</span>}
                        <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>XT: {xtLabel}</span>
                        {!day.xtRange && <strong style={{ fontSize: 12 }}>{formatVolume(total)}</strong>}
                      </div>
                    );
                  })()}

                  {day.coachNote && (
                    <div className="day-coach-note">
                      <span className="day-coach-note-label">Coach</span>
                      {day.coachNote}
                    </div>
                  )}

                  {day.date <= todayStr && (() => {
                    const isAmPm = /\bam\s*:/i.test(day.rawWorkout) && /\bpm\s*:/i.test(day.rawWorkout);
                    if (isAmPm) {
                      const amLogged = !!sessions[day.date]?.am;
                      const pmLogged = !!sessions[day.date]?.pm;
                      return (
                        <div style={{ display: 'flex', gap: 4, flexDirection: 'column', marginTop: 4 }}>
                          <button className="day-log-btn" onClick={() => setLogModalDay({ ...day, startSession: 'am' })}>
                            {amLogged ? '✓ Edit AM' : '+ Log AM'}
                          </button>
                          <button className="day-log-btn" style={pmLogged ? {} : { opacity: 0.75 }} onClick={() => setLogModalDay({ ...day, startSession: 'pm' })}>
                            {pmLogged ? '✓ Edit PM' : '+ Log PM'}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button className="day-log-btn" onClick={() => setLogModalDay(day)}>
                        {log ? 'Edit Log' : '+ Log'}
                      </button>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Tab renderers ─────────────────────────────────────────────────

  function renderTrainingTab() {
    const EQUIPMENT_OPTIONS = [
      { key: 'track', label: 'Track' },
      { key: 'treadmill', label: 'Treadmill' },
      { key: 'roads', label: 'Roads / Trails' },
      { key: 'weight_room', label: 'Weight Room' },
      { key: 'pool', label: 'Pool' },
      { key: 'gym', label: 'Gym' },
    ];

    function datesInRange(start, end) {
      if (!start || !end || end < start) return [];
      const dates = [];
      const d = new Date(start + 'T00:00:00');
      const last = new Date(end + 'T00:00:00');
      while (d <= last) {
        dates.push(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
      }
      return dates;
    }

    function fmtVacDate(dateStr) {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    const rangeForForm = datesInRange(vacStart, vacEnd);
    const thisWeekMiles = weekBars[15]?.miles ?? 0;
    const prevWeeks = weekBars.slice(3, 15).map(w => w.miles);
    const avgMiles = prevWeeks.length ? prevWeeks.reduce((a, b) => a + b, 0) / prevWeeks.length : 0;
    const trend = avgMiles > 0.5 ? Math.round(((thisWeekMiles - avgMiles) / avgMiles) * 100) : null;
    const gridLines = [0.25, 0.5, 0.75, 1].map(pct => Math.round(maxBarMiles * pct * 10) / 10);

    return (
      <>
        {/* Qualifier Banner */}
        {qualifiers.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #92400e 0%, var(--gold) 60%, #92400e 100%)',
            borderRadius: 'var(--radius-md)', padding: '16px 24px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{ fontSize: 28 }}>&#127942;</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--navy)', marginBottom: 4 }}>
                Qualification Standards Achieved
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {qualifiers.map((q, i) => (
                  <span key={i} style={{
                    background: 'rgba(0,24,44,0.12)', borderRadius: 999, padding: '3px 10px',
                    fontSize: 12, fontWeight: 700, color: 'var(--navy)',
                  }}>
                    {EVENTS[q.event]?.label ?? q.event} &mdash; {q.standardLabel} ({q.sublabel})
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Coach info */}
        {coachName && (
          <div className="section-card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px' }}>
            <div className="profile-avatar" style={{ width: 52, height: 52, fontSize: 16, flexShrink: 0, cursor: 'default' }}>
              {coachAvatarUrl
                ? <img src={coachAvatarUrl} alt={coachName} />
                : coachName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
              }
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>
                Coach {coachName.split(' ').slice(-1)[0]}
                {coachCredentials && (
                  <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--gray-400)', marginLeft: 8 }}>{coachCredentials}</span>
                )}
              </div>
              {coachBio && <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>{coachBio}</div>}
            </div>
          </div>
        )}

        {/* Next race */}
        {nextRace && (
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-card-header">
              <span className="section-card-title">Next Race</span>
              <Link to="/athlete/races" style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'none', fontWeight: 600 }}>View All</Link>
            </div>
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className={`race-priority-badge priority-${nextRace.priority}`} style={{ fontSize: 16, width: 36, height: 36 }}>
                {nextRace.priority}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{nextRace.name}</div>
                <div className="cell-muted" style={{ fontSize: 13 }}>
                  {getEventLabels(nextRace.event)}
                  {nextRace.location && ` \xb7 ${nextRace.location}`}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>{formatRaceDateRange(nextRace.race_date, nextRace.race_date_end)}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: daysUntil(nextRace.race_date) <= 7 ? 'var(--gold)' : 'var(--navy)' }}>
                  {daysUntil(nextRace.race_date) === 0 ? 'Today'
                    : daysUntil(nextRace.race_date) === 1 ? 'Tomorrow'
                    : `${daysUntil(nextRace.race_date)} days away`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2-week training view */}
        <div className="section-card" style={{ marginBottom: 16 }}>
          <div className="section-card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="section-card-title">Training Plan</span>
            </div>
            <div className="week-nav-bar">
              <button className="week-nav-btn" onClick={() => setWeekOffset(o => o - 1)}>&#8592;</button>
              <span className="week-nav-label">
                {weekRangeLabel(weekOffset)} &nbsp;&middot;&nbsp; {weekRangeLabel(weekOffset + 1)}
              </span>
              <button className="week-nav-btn" onClick={() => setWeekOffset(o => o + 1)}>&#8594;</button>
            </div>
          </div>

          {renderWeekSection(weekWorkouts, trainingLogs, trainingLogSessions, false, loading)}
          {renderWeekSection(week2Workouts, trainingLogs2, trainingLogSessions2, true, loading2)}
        </div>

        {/* Training Volume Chart */}
        <div className="section-card" style={{ marginTop: 16, marginBottom: 16 }}>
          <div className="vol-header">
            <span className="section-card-title" style={{ marginBottom: 0 }}>Training Volume</span>
            <div className="vol-stats-row">
              <div className="vol-stat">
                <span className="vol-stat-value">{thisWeekMiles > 0.01 ? (Math.round(thisWeekMiles * 10) / 10) + ' mi' : '\u2014'}</span>
                <span className="vol-stat-label">This Week</span>
              </div>
              <div className="vol-stat-divider" />
              <div className="vol-stat">
                <span className="vol-stat-value">{avgMiles > 0.5 ? (Math.round(avgMiles * 10) / 10) + ' mi' : '\u2014'}</span>
                <span className="vol-stat-label">12-wk Avg</span>
              </div>
              {trend !== null && (
                <>
                  <div className="vol-stat-divider" />
                  <div className="vol-stat">
                    <span className={`vol-stat-value${trend >= 0 ? ' vol-trend-up' : ' vol-trend-down'}`}>
                      {trend >= 0 ? '\u2191' : '\u2193'} {Math.abs(trend)}%
                    </span>
                    <span className="vol-stat-label">vs Avg</span>
                  </div>
                </>
              )}
            </div>
          </div>
          {historyLogs.length === 0 ? (
            <p className="cell-muted" style={{ padding: '16px 24px', fontSize: 13 }}>Log workouts to see your training volume here.</p>
          ) : (
            <div className="vol-chart-wrap">
              <div className="vol-chart">
                <div className="vol-grid">
                  {gridLines.map((val, gi) => (
                    <div key={gi} className="vol-grid-line" style={{ bottom: `${(val / maxBarMiles) * 100}%` }}>
                      <span className="vol-grid-label">{val}</span>
                    </div>
                  ))}
                </div>
                <div className="vol-bars">
                  {weekBars.map((bar, i) => {
                    const isCurrent = i === 15;
                    const heightPct = bar.miles > 0.01 ? Math.max(2, (bar.miles / maxBarMiles) * 100) : 0;
                    return (
                      <div key={i} className="vol-bar-col">
                        <div className="vol-bar-wrap">
                          {bar.miles > 0.01 && (
                            <div className={`vol-bar-tooltip${isCurrent ? ' vol-bar-tooltip--current' : ''}`}>
                              {Math.round(bar.miles * 10) / 10} mi
                            </div>
                          )}
                          <div className={`vol-bar${isCurrent ? ' vol-bar--current' : ''}`} style={{ height: `${heightPct}%` }} />
                        </div>
                        <div className={`vol-bar-label${isCurrent ? ' vol-bar-label--current' : ''}`}>{bar.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Best Times */}
        {Object.keys(bestSplits).length > 0 && (
          <div className="section-card" style={{ marginTop: 16, marginBottom: 16 }}>
            <div className="section-card-header">
              <span className="section-card-title">Best Times</span>
              <span className="cell-muted" style={{ fontSize: 12 }}>From training logs (last 8 weeks)</span>
            </div>
            <div className="best-splits-grid">
              {Object.entries(bestSplits)
                .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
                .map(([dist, record]) => (
                  <div key={dist} className="best-split-item">
                    <div className="best-split-dist">{dist}</div>
                    <div className="best-split-time">{record.timeStr}</div>
                    <div className="best-split-date">{formatShortDate(record.logDate)}</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Pending race results */}
        {pendingResults.length > 0 && (
          <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid var(--gold)' }}>
            <div className="section-card-header">
              <span className="section-card-title">Log Race Results</span>
            </div>
            <div>
              {pendingResults.map(race => (
                <div key={race.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{race.name}</div>
                      <div className="cell-muted" style={{ fontSize: 12 }}>
                        {getEventLabels(race.event)} &middot; {formatRaceDateRange(race.race_date, race.race_date_end)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        className="form-input" style={{ width: 100, padding: '6px 10px', fontSize: 13 }}
                        type="text" placeholder="4:32.56"
                        value={resultInputs[race.id] ?? ''}
                        onChange={e => setResultInputs(prev => ({ ...prev, [race.id]: e.target.value }))}
                      />
                      <button
                        className="btn btn-primary" style={{ width: 'auto', padding: '7px 16px', fontSize: 13 }}
                        onClick={() => handleLogResult(race)} disabled={resultSaving === race.id}
                      >
                        {resultSaving === race.id ? 'Saving...' : 'Log'}
                      </button>
                    </div>
                  </div>
                  {resultErrors[race.id] && (
                    <p style={{ margin: '4px 0 0 44px', fontSize: 12, color: 'var(--red, #dc2626)' }}>{resultErrors[race.id]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Comments */}
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">General Comments</span>
            <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>Visible to your coach &mdash; auto-saves</span>
          </div>
          <div style={{ padding: '16px 24px' }}>
            <textarea
              className="form-input"
              style={{ minHeight: 100, resize: 'vertical', fontSize: 14 }}
              placeholder="Notes for your coach, how you've been feeling, training feedback..."
              value={proComments}
              onChange={e => setProComments(e.target.value)}
              onBlur={handleSaveComments}
            />
            {commentsSaving && <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>Saving...</p>}
          </div>
        </div>

        {/* Vacation */}
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Calendar & Events</span>
            <button className="btn-xs btn-xs-ghost" onClick={() => { setShowVacForm(v => !v); setVacError(''); }}>
              {showVacForm ? 'Cancel' : '+ Add Trip'}
            </button>
          </div>

          {showVacForm && (
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
              <form onSubmit={handleAddVacation}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Start Date</label>
                    <input className="form-input" type="date" value={vacStart}
                      onChange={e => { setVacStart(e.target.value); setVacBlackouts([]); }} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">End Date</label>
                    <input className="form-input" type="date" value={vacEnd} min={vacStart}
                      onChange={e => { setVacEnd(e.target.value); setVacBlackouts([]); }} required />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">What will you have access to?</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {EQUIPMENT_OPTIONS.map(opt => (
                      <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={vacEquipment.includes(opt.key)}
                          onChange={() => setVacEquipment(prev => prev.includes(opt.key) ? prev.filter(k => k !== opt.key) : [...prev, opt.key])} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
                {rangeForForm.length > 1 && (
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Days you can't run at all</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {rangeForForm.map(dateStr => (
                        <label key={dateStr} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={vacBlackouts.includes(dateStr)}
                            onChange={() => setVacBlackouts(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr])} />
                          {fmtVacDate(dateStr)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">Notes (optional)</label>
                  <input className="form-input" type="text" value={vacNotes} onChange={e => setVacNotes(e.target.value)}
                    placeholder="Hotel gym only, no long runs, etc." />
                </div>
                {vacError && <p className="form-error" style={{ marginTop: 0, marginBottom: 8 }}>{vacError}</p>}
                <button className="btn btn-primary" type="submit" disabled={vacSaving} style={{ width: 'auto', padding: '8px 20px' }}>
                  {vacSaving ? 'Saving...' : 'Save Trip'}
                </button>
              </form>
            </div>
          )}

          {vacations.length === 0 && !showVacForm ? (
            <div className="section-card-body" style={{ color: 'var(--gray-400)', fontSize: 13 }}>
              No upcoming trips. Click "+ Add Trip" to let your coach know about travel.
            </div>
          ) : (
            <div style={{ padding: vacations.length > 0 ? '8px 0' : 0 }}>
              {vacations.map(v => {
                const equipLabels = (v.available_equipment ?? []).map(k => EQUIPMENT_OPTIONS.find(o => o.key === k)?.label ?? k);
                const blackoutLabels = (v.blackout_dates ?? []).sort().map(d =>
                  new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                );
                return (
                  <div key={v.id} className="vac-item">
                    <div className="vac-item-dates">
                      {new Date(v.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {v.start_date !== v.end_date && ` \u2013 ${new Date(v.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                    </div>
                    <div className="vac-item-body">
                      {equipLabels.length > 0 && (
                        <div className="vac-item-row"><span className="vac-item-label">Available:</span>{equipLabels.join(', ')}</div>
                      )}
                      {blackoutLabels.length > 0 && (
                        <div className="vac-item-row"><span className="vac-item-label">No-run days:</span>{blackoutLabels.join(', ')}</div>
                      )}
                      {v.notes && <div className="vac-item-notes">"{v.notes}"</div>}
                    </div>
                    <button className="vac-item-delete" onClick={() => handleDeleteVacation(v.id)} title="Remove">&times;</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Media for Social */}
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Media for Social</span>
            <label style={{ cursor: 'pointer' }}>
              <input type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleMediaUpload} disabled={mediaUploading} />
              <span className="btn-xs btn-xs-ghost">{mediaUploading ? 'Uploading\u2026' : '+ Upload'}</span>
            </label>
          </div>
          <div style={{ padding: '12px 16px' }}>
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 16 }}>
              Upload photos or videos for the coaching staff to use on Apex Elite's social media.
            </p>
            {mediaError && <p style={{ fontSize: 12, color: 'var(--red, #dc2626)', marginBottom: 12 }}>{mediaError}</p>}
            {proMedia.length === 0 ? (
              <div className="section-card-body" style={{ padding: '24px 0' }}>No media uploaded yet.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
                {proMedia.map(m => (
                  <div key={m.id} style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--gray-200)', background: 'var(--white)' }}>
                    <div style={{ position: 'relative', paddingTop: '75%', background: 'var(--gray-100)' }}>
                      {/\.(mp4|mov|webm)$/i.test(m.file_url)
                        ? <video src={m.file_url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} muted />
                        : <img src={m.file_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      }
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <input
                        className="form-input" style={{ fontSize: 12, padding: '5px 8px', marginBottom: 6 }}
                        type="text" placeholder="Add a caption..."
                        value={mediaCaptions[m.id] ?? ''}
                        onChange={e => setMediaCaptions(prev => ({ ...prev, [m.id]: e.target.value }))}
                        onBlur={() => handleMediaCaptionBlur(m.id)}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                          {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <button onClick={() => handleDeleteMedia(m.id)}
                          style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                          title="Delete">&times;</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </>
    );
  }

  function renderStandardsTab() {
    const allEventSet = new Set();
    QUAL_STANDARDS.forEach(std => Object.keys(std.standards).forEach(ev => allEventSet.add(ev)));
    const eventList = Array.from(allEventSet).sort();

    const qualMap = new Set(qualifiers.map(q => `${q.standardKey}::${q.event}`));
    const genderToShow = stdGenderFilter !== 'all' ? stdGenderFilter : (gender ?? 'male');

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p className="page-eyebrow">Standards</p>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>Qualification Standards</h2>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['all', 'male', 'female'].map(g => (
              <button
                key={g}
                onClick={() => setStdGenderFilter(g)}
                style={{
                  padding: '6px 16px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'var(--font)', border: 'none',
                  background: stdGenderFilter === g ? 'var(--navy)' : 'var(--gray-100)',
                  color: stdGenderFilter === g ? 'var(--white)' : 'var(--gray-700)',
                }}
              >
                {g === 'all' ? 'Both Genders' : g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {qualifiers.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #92400e, var(--gold))',
            borderRadius: 'var(--radius-md)', padding: '14px 20px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 22 }}>&#127942;</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>
                You have achieved {qualifiers.length} qualification standard{qualifiers.length !== 1 ? 's' : ''}!
              </div>
              <div style={{ fontSize: 12, color: 'rgba(0,24,44,0.7)' }}>Gold highlighted rows below</div>
            </div>
          </div>
        )}

        {QUAL_STANDARDS.map(std => {
          const stdEvents = eventList.filter(ev => std.standards[ev]);
          if (!stdEvents.length) return null;
          return (
            <div key={std.key} className="section-card" style={{ marginBottom: 20 }}>
              <div className="section-card-header">
                <span className="section-card-title">{std.label}</span>
                <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{std.sublabel}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--gray-100)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 700, color: 'var(--navy)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Event
                      </th>
                      {(stdGenderFilter === 'all' || stdGenderFilter === 'male') && (
                        <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: 700, color: 'var(--navy)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Men's Standard
                        </th>
                      )}
                      {(stdGenderFilter === 'all' || stdGenderFilter === 'female') && (
                        <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: 700, color: 'var(--navy)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Women's Standard
                        </th>
                      )}
                      {gender && (
                        <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: 700, color: 'var(--navy)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Your Best
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {stdEvents.map(ev => {
                      const isQual = qualMap.has(`${std.key}::${ev}`);
                      const myBest = benchmarks
                        .filter(b => b.event === ev)
                        .reduce((a, b) => (!a || b.time_seconds < a.time_seconds) ? b : a, null);
                      return (
                        <tr
                          key={ev}
                          style={{
                            borderBottom: '1px solid var(--gray-100)',
                            background: isQual ? 'rgba(252,198,37,0.12)' : undefined,
                          }}
                        >
                          <td style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isQual && <span style={{ fontSize: 13 }}>&#127942;</span>}
                            <span style={{ fontWeight: isQual ? 700 : 500, color: 'var(--navy)' }}>
                              {EVENTS[ev]?.label ?? ev}
                            </span>
                          </td>
                          {(stdGenderFilter === 'all' || stdGenderFilter === 'male') && (
                            <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: isQual && genderToShow === 'male' ? 'var(--gold-dark)' : 'var(--gray-700)' }}>
                              {formatStdTime(std.standards[ev]?.male)}
                            </td>
                          )}
                          {(stdGenderFilter === 'all' || stdGenderFilter === 'female') && (
                            <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: isQual && genderToShow === 'female' ? 'var(--gold-dark)' : 'var(--gray-700)' }}>
                              {formatStdTime(std.standards[ev]?.female)}
                            </td>
                          )}
                          {gender && (
                            <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: isQual ? 700 : 400, color: isQual ? 'var(--gold-dark)' : 'var(--gray-500)' }}>
                              {myBest ? formatTime(myBest.time_seconds) : '\u2014'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  function renderGearTab() {
    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <p className="page-eyebrow">Gear</p>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>Gear Requests</h2>
        </div>

        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Request Gear</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <form onSubmit={handleGearSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Item Type</label>
                  <select className="form-input form-select" value={gearItemType} onChange={e => setGearItemType(e.target.value)}>
                    {GEAR_ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Model</label>
                  <input className="form-input" type="text" value={gearModel} onChange={e => setGearModel(e.target.value)} placeholder="Alphafly 3, Metaspeed..." />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Size</label>
                  <input className="form-input" type="text" value={gearSize} onChange={e => setGearSize(e.target.value)} placeholder="US 10, M, L..." />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Notes (optional)</label>
                <textarea className="form-input" style={{ minHeight: 70, resize: 'vertical' }} value={gearNotes} onChange={e => setGearNotes(e.target.value)} placeholder="Color preference, special requirements..." />
              </div>
              {gearError && <p className="form-error" style={{ marginBottom: 8 }}>{gearError}</p>}
              <button className="btn btn-primary" type="submit" disabled={gearSaving} style={{ width: 'auto', padding: '10px 24px' }}>
                {gearSaving ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>

        {gearRequests.length > 0 ? (
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">My Requests</span>
            </div>
            {gearRequests.map(req => (
              <div key={req.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 2 }}>
                    {req.item_type}
                    {req.model && <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}> &mdash; {req.model}</span>}
                  </div>
                  {req.size && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Size: {req.size}</div>}
                  {req.notes && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{req.notes}</div>}
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                    Requested {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <span style={{
                  ...STATUS_STYLES[req.status] ?? STATUS_STYLES.pending,
                  display: 'inline-flex', alignItems: 'center', padding: '3px 12px',
                  borderRadius: 999, fontSize: 11, fontWeight: 700, flexShrink: 0, textTransform: 'capitalize',
                }}>
                  {req.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="section-card">
            <div className="section-card-body">No gear requests yet. Submit your first request above.</div>
          </div>
        )}
      </>
    );
  }

  function renderProfileTab() {
    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <p className="page-eyebrow">Profile</p>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>My Pro Profile</h2>
        </div>

        {/* Photo */}
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Profile Photo</span>
          </div>
          <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 20 }}>
            <label style={{ cursor: 'pointer', flexShrink: 0 }}>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} disabled={avatarUploading} />
              <div className="profile-avatar" style={{ width: 80, height: 80, fontSize: 24 }}>
                {avatarUrl
                  ? <img src={avatarUrl} alt={profile?.full_name} />
                  : (profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) ?? '?')
                }
                <div className="profile-avatar-overlay">{avatarUploading ? '\u2026' : 'Upload'}</div>
              </div>
            </label>
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)', margin: 0 }}>
                {avatarUploading ? 'Uploading\u2026' : avatarUrl ? 'Change Photo' : 'Upload Photo'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 4 }}>
                Used on your athlete card and social media features.
              </p>
            </div>
          </div>
        </div>

        {/* USATF */}
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">USATF Association</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
              <input
                type="checkbox" checked={usatfChecked}
                onChange={e => handleUsatfChange(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>I am associated with Apex Elite on USATF</div>
                <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 2 }}>
                  Check this box if you are listed under Apex Elite's USATF club registration.
                </div>
              </div>
            </label>
          </div>
        </div>

      </>
    );
  }

  return (
    <Layout>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <p className="page-eyebrow">Pro Dashboard</p>
        <h1 className="page-title">Welcome back, {profile?.full_name?.split(' ')[0]}.</h1>
      </div>

      {/* Tab content */}
      {activeTab === 'training' && renderTrainingTab()}
      {activeTab === 'standards' && renderStandardsTab()}
      {activeTab === 'gear' && renderGearTab()}
      {activeTab === 'profile' && renderProfileTab()}

      {/* Training log modal */}
      {logModalDay && (
        <TrainingLogModal
          day={logModalDay}
          paces={paces}
          existingLog={trainingLogSessions[logModalDay.date]?.full ?? trainingLogSessions2[logModalDay.date]?.full ?? null}
          existingAmLog={trainingLogSessions[logModalDay.date]?.am ?? trainingLogSessions2[logModalDay.date]?.am ?? null}
          existingPmLog={trainingLogSessions[logModalDay.date]?.pm ?? trainingLogSessions2[logModalDay.date]?.pm ?? null}
          startSession={logModalDay.startSession}
          onSave={handleLogSaved}
          onClose={() => setLogModalDay(null)}
        />
      )}
    </Layout>
  );
}
